import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { downloadAuthed } from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Receipt, DownloadSimple, IdentificationCard, Signature, FolderSimple, Warning, Lock } from "@phosphor-icons/react";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function AdminUsageBillingPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (me === false) navigate("/login");
    else if (me && !me.is_admin) { toast.error("Admins only"); navigate("/"); }
  }, [me, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/usage-billing`, { params: { start, end } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load report"); }
    finally { setLoading(false); }
  }, [start, end]);

  const [syncing, setSyncing] = useState(false);
  const recompute = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await API.post(`/usage-billing/recompute-lines`);
      toast.success(`Synced billing lines across ${data.cases_processed} case${data.cases_processed === 1 ? "" : "s"}`);
      await load();
      loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to sync billing lines"); }
    finally { setSyncing(false); }
  }, [load]); // eslint-disable-line

  const [audit, setAudit] = useState([]);
  const loadAudit = useCallback(async () => {
    try {
      const { data } = await API.get(`/usage-billing/lock-audit`, { params: { limit: 50 } });
      setAudit(data.entries || []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { if (me?.is_admin) loadAudit(); }, [me]); // eslint-disable-line

  useEffect(() => { if (me?.is_admin) load(); }, [me]); // eslint-disable-line

  const qs = `?start=${start}&end=${end}`;
  const [includeMissing, setIncludeMissing] = useState(false);
  const exportQs = `${qs}${includeMissing ? "&include_missing=1" : ""}`;
  const s = data?.summary;

  const cards = s ? [
    { key: "id", label: "ID Checks", icon: IdentificationCard, count: s.id_checks.count, unit: s.id_checks.unit, subtotal: s.id_checks.subtotal, color: "text-blue-600" },
    { key: "sign", label: "Document Signing", icon: Signature, count: s.doc_signing.count, unit: s.doc_signing.unit, subtotal: s.doc_signing.subtotal, color: "text-emerald-600" },
    { key: "case", label: "Case Management", icon: FolderSimple, count: s.case_management.count, unit: s.case_management.unit, subtotal: s.case_management.subtotal, color: "text-indigo-600" },
  ] : [];

  const Section = ({ title, rows }) => (
    <div className="mb-6" data-testid={`usage-section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
      <h3 className="text-sm font-semibold text-slate-800 mb-2">{title} <span className="text-slate-400 font-normal">({rows?.length || 0})</span></h3>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Case Ref</th><th className="text-left px-3 py-2">Detail</th><th className="text-right px-3 py-2">Amount</th></tr>
          </thead>
          <tbody>
            {(!rows || rows.length === 0) ? (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400 text-xs">No items in this date range</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.date}</td>
                <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{r.case_ref}</td>
                <td className="px-3 py-2 text-slate-600 truncate max-w-[340px]">{r.detail}</td>
                <td className="px-3 py-2 text-right text-slate-900">{money(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="usage-billing-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Receipt weight="bold" className="w-6 h-6" /> Usage &amp; Billing</h1>
            <p className="text-sm text-slate-500 mt-1">Chargeable items consumed in ConveyHub, priced for billing David J Foster &amp; Co. Every line carries the case reference for client-ledger reconciliation.</p>
          </div>
        </div>

        {data?.missing_case_mgmt?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="missing-casemgmt-banner">
            <Warning weight="fill" className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {data.missing_case_mgmt.length} active case{data.missing_case_mgmt.length === 1 ? "" : "s"} missing the case-management fee
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                These won't appear in billing until the fee line is added. Refs: {data.missing_case_mgmt.slice(0, 8).map((m) => m.case_ref).join(", ")}{data.missing_case_mgmt.length > 8 ? `, +${data.missing_case_mgmt.length - 8} more` : ""}.
              </p>
            </div>
            <Button onClick={recompute} disabled={syncing} className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white h-9" data-testid="missing-casemgmt-fix-btn">
              {syncing ? "Fixing…" : "Add missing fees"}
            </Button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="usage-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="usage-end" /></div>
          <Button onClick={load} disabled={loading} data-testid="usage-apply">{loading ? "Loading…" : "Apply"}</Button>
          <Button variant="outline" onClick={recompute} disabled={syncing} data-testid="usage-recompute" title="Re-apply the case-management, ID and e-signing fee lines across every current case. Fixes cases opened via instruction/quote that were missing their billing lines.">{syncing ? "Syncing…" : "Sync billing lines"}</Button>
          <div className="flex-1" />
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none mr-1" data-testid="usage-include-missing-toggle" title="Append the list of active cases missing the case-management fee to the export, for reconciliation.">
            <input type="checkbox" checked={includeMissing} onChange={(e) => setIncludeMissing(e.target.checked)} className="accent-slate-800" data-testid="usage-include-missing-checkbox" />
            Include missing-fee list
          </label>
          <Button variant="outline" onClick={() => downloadAuthed(`/usage-billing/export.csv${exportQs}`, `conveyhub-usage-${start}-to-${end}.csv`)} data-testid="usage-csv"><DownloadSimple className="w-4 h-4 mr-1" /> CSV</Button>
          <Button variant="outline" onClick={() => downloadAuthed(`/usage-billing/export.pdf${exportQs}`, `conveyhub-usage-${start}-to-${end}.pdf`)} data-testid="usage-pdf"><DownloadSimple className="w-4 h-4 mr-1" /> PDF</Button>
        </div>

        {s && (
          <div className="grid sm:grid-cols-4 gap-3 mb-8">
            {cards.map((c) => (
              <div key={c.key} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`usage-card-${c.key}`}>
                <c.icon className={`w-5 h-5 ${c.color} mb-1`} weight="bold" />
                <div className="text-xs text-slate-500">{c.label}</div>
                <div className="text-xl font-semibold text-slate-900">{money(c.subtotal)}</div>
                <div className="text-[11px] text-slate-400">
                  {c.unit == null ? `${c.count} item${c.count === 1 ? "" : "s"} · varies` : `${c.count} @ ${money(c.unit)}`}
                </div>
              </div>
            ))}
            <div className="bg-slate-900 text-white rounded-lg p-4" data-testid="usage-card-total">
              <div className="text-xs text-slate-300">Grand total</div>
              <div className="text-2xl font-semibold">{money(s.grand_total)}</div>
              <div className="text-[11px] text-slate-400">{start} → {end}</div>
            </div>
          </div>
        )}

        {data && (
          <>
            <Section title="ID Checks" rows={data.id_checks} />
            <Section title="Document Signing" rows={data.doc_signing} />
            <Section title="Case Management" rows={data.case_management} />
          </>
        )}

        {audit.length > 0 && (
          <div className="mb-6" data-testid="lock-audit-section">
            <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
              <Lock weight="bold" className="w-4 h-4 text-slate-500" /> Locked fee activity
              <span className="text-slate-400 font-normal">({audit.length})</span>
            </h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Who</th><th className="text-left px-3 py-2">Fee line</th><th className="text-left px-3 py-2">Action</th></tr>
                </thead>
                <tbody>
                  {audit.map((a, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`lock-audit-row-${i}`}>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleString("en-GB") : "—"}</td>
                      <td className="px-3 py-2 text-slate-700">{a.user_name || a.user_email}</td>
                      <td className="px-3 py-2 text-slate-600">{a.label}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${a.action === "deleted" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                          {a.action === "deleted" ? "Deleted (partner)" : "Blocked"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
